// shared-lib/cabinet-runs/familyOffering.js
//
// What a cabinet FAMILY sells for the look: its door designs, paint colours and
// wood finishes (design: claude-docs/2026-10-04-cabinet-family-offerings-
// proposal.md). Every surface that offers or chooses a cabinet look reads it
// from here — the panel, the finish picker, the Design Center, the style tools
// — so they cannot disagree about what a family sells.
//
// A family file says `"all"` for a list it does not restrict. The three
// generic families say "all" for doors and paint, and list the five cabinet
// woods the app offered before any vendor family brought its own — so a
// vendor's woods stay with the vendor, and every generic run keeps exactly
// the choices it had.
//
// Existing designs are FLAGGED, never rewritten: `lookNotOffered` reports what
// a cabinet wears that its family does not sell, and nothing here changes a
// stored cabinet on its own. Only something CHOOSING a look anew moves it
// inside the offering — a style tool (`mapLookToOffering`), a door pick
// (`lookForDoorPick`), a family switch (`lookForFamily`).
//
// A door design may sell only SOME of its family's finishes (Tasoro's shaker
// comes in four paints, its flat panel in woodgrains): `design.finishes` lists
// them, and `offeringForLook` narrows an offering to the finishes the look's
// door is sold in. Everything that judges or chooses a FINISH goes through it,
// so a colour is "offered" only with a door it is actually sold on.
//
// Pure. A look is the appearance-bundle shape a cabinet carries:
// `{ doorStyle, doorDesign, paintColor, paintFinish, texture: { textureId } }`.

const { familyOf } = require('./families');
const {
  DRAWN_DOOR_STYLES,
  DRAWN_DOOR_STYLE_IDS,
  resolveDoorStyle,
  isDoorlessCabinet,
} = require('./doorStyles');
const { CABINET_PAINT_COLORS } = require('../constants/paintColors');
const { VENDOR_SIZE_SETS } = require('./vendorSizes');
const { CORNER_BLIND_SETS } = require('../kitchen-assembly/cornerBlind');
// `spec.depths`: "any" — boxes are built to whatever depth a stretch needs (a
// custom shop). Omitted — the standard depths only, 24" bases and 12" uppers
// (user ruling 2026-10-23: "most semi-custom cab lines only have std depths").
const DEPTH_RULES = ['any'];
const { narrowToCompany } = require('./companyOffering');

const sameHex = (a, b) =>
  typeof a === 'string' &&
  typeof b === 'string' &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

const slug = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Every drawn style as a design: what "all" door designs means. */
const ALL_DOOR_DESIGNS = DRAWN_DOOR_STYLES.map((d) => ({
  id: d.id,
  name: d.name,
  renders: d.id,
}));

/** The shared cabinet palette as offering entries: what "all" paint means. */
const ALL_PAINT = CABINET_PAINT_COLORS.map((c) => ({
  id: slug(c.name),
  name: c.name,
  hex: c.hex,
  category: c.category,
}));

/**
 * A family's offering, resolved to lists.
 *
 * `restricts` says which lists LIMIT the look. An unrestricted door or paint
 * list is still a full list (the four drawn styles, the shared palette) — it is
 * what the pickers show — but anything at all is offered under it, including a
 * colour a style tool chose that is not in the palette. Woods have no "all"
 * list (the catalog is the list), so an unrestricted wood list is `null`.
 *
 * With `companyOffering` (the company's `offered` flags, issue #2460) the
 * lists are narrowed to what the company offers (`companyOffering.js`); with
 * none, or flags that remove nothing, it is the family's offering exactly.
 */
function offeringOf(family, companyOffering) {
  return narrowToCompany(familyOfferingOf(family), companyOffering);
}

/** What the family itself sells, before the company says what it offers. */
function familyOfferingOf(family) {
  const o = (family && family.offering) || {};
  const restricts = {
    door: Array.isArray(o.doorDesigns),
    paint: Array.isArray(o.paint),
    wood: Array.isArray(o.woods),
  };
  return {
    familyId: (family && family.id) || null,
    familyName: (family && family.name) || null,
    // Whether the panel should SAY why the choices are narrowed. A vendor's
    // range is news to the user; the generic families' five woods are the
    // only cabinet woods the app has ever offered them, so there is nothing
    // to explain (`cabinetOffering.offeringNote`).
    explain: !!(family && family.vendor),
    restricts,
    doorDesigns: restricts.door ? o.doorDesigns : ALL_DOOR_DESIGNS,
    paint: restricts.paint ? o.paint : ALL_PAINT,
    woods: restricts.wood ? o.woods : null,
    defaults: o.defaults || null,
  };
}

/**
 * The offering of the family a run draws from (unset → the default family),
 * narrowed to `companyOffering` (`companyOffering.js`) when one is given.
 */
function offeringForRun(run, companyOffering) {
  return offeringOf(familyOf(run), companyOffering);
}

/**
 * The offering a cabinet OBJECT draws from: its run's family when it has one
 * (`storedRun`, read off the room — the object's `runFamily` is only the stamp
 * of the last materialize), else the stamp, else — for a cabinet in no run —
 * nothing is restricted.
 */
function offeringForCabinet(cabinet, storedRun, companyOffering) {
  if (storedRun) return offeringForRun(storedRun, companyOffering);
  if (cabinet && cabinet.runId) {
    return offeringForRun({ family: cabinet.runFamily }, companyOffering);
  }
  return offeringOf(null);
}

/**
 * The design a look's door is, under an offering, or null when none matches.
 *
 * 1. A design this offering sells, still drawn as the cabinet's `doorStyle`,
 *    is that design.
 * 2. One it sells that no longer matches the drawn style is STALE — a tool
 *    that sets only the style (the generator, a style preset) left it behind —
 *    so the door is matched by what it draws.
 * 3. A bare drawn-style id (what a generic family writes: `shaker`) is not a
 *    product, so it too is matched by what it draws.
 * 4. Another vendor's design matches nothing under a family that restricts
 *    its doors: it is not this family's, even if it draws the same. A family
 *    that sells every door (the generic ones) sells what it draws, so there
 *    it is matched by that — a Tasoro shaker is a Shaker to Generic Stock.
 * With no recorded design (legacy data), the door is matched by what it draws.
 */
function designForLook(offering, look) {
  const designs = offering.doorDesigns;
  const drawn = resolveDoorStyle(look && look.doorStyle);
  const byDrawn = () => designs.find((d) => d.renders === drawn) || null;
  const recorded = look && look.doorDesign;
  if (!recorded) return byDrawn();
  const named = designs.find((d) => d.id === recorded);
  // A GLASS door is drawn `glass-front` whatever design it is: a glass Houston
  // upper is still a Houston cabinet, in its colour — when Houston sells glass
  // (`designSellsGlass`). A design that does not is not what a glass door is.
  if (
    offering.restricts.door &&
    named &&
    drawn === GLASS_FRONT &&
    named.renders !== GLASS_FRONT
  )
    return named.glass === true ? named : null;
  if (named) return named.renders === drawn ? named : byDrawn();
  return !offering.restricts.door || DRAWN_DOOR_STYLE_IDS.includes(recorded)
    ? byDrawn()
    : null;
}

/** The drawn style a glass door is. */
const GLASS_FRONT = 'glass-front';

/**
 * Does `family` sell glass doors on `design` (plan C12)? A family that LISTS
 * its door designs sells glass only on the designs flagged `glass: true`
 * (Venus: all but Slim; Tasoro: none). A family whose designs are "all" sells
 * any door, so glass too, whatever the design.
 */
function designSellsGlass(family, design) {
  const designs = family && family.offering && family.offering.doorDesigns;
  if (!Array.isArray(designs)) return true;
  return !!design && design.glass === true;
}

/**
 * `offering` narrowed to the finishes `design` is sold in. A design with no
 * `finishes` sells every finish its family does, so the offering comes back
 * as it is. One that lists them sells exactly those — a kind it leaves out
 * (no `woods`) it is not sold in at all — and restricts that kind even under a
 * family that otherwise sells everything. `design` (the design's name) rides
 * along for the panel's note.
 */
function offeringForDesign(offering, design) {
  if (!design || !design.finishes) return offering;
  const paintIds = design.finishes.paint || [];
  const woodIds = design.finishes.woods || [];
  const woods = offering.woods
    ? offering.woods.filter((w) => woodIds.includes(w))
    : woodIds;
  const ownDefault = design.defaultFinish || null;
  const familyDefault = offering.defaults && offering.defaults.finish;
  const sold = (fin) =>
    !!fin &&
    ((fin.paint && paintIds.includes(fin.paint)) ||
      (fin.wood && woods.includes(fin.wood)));
  return {
    ...offering,
    design: design.name || design.id,
    restricts: { ...offering.restricts, paint: true, wood: true },
    paint: offering.paint.filter((p) => paintIds.includes(p.id)),
    woods,
    defaults: {
      ...(offering.defaults || {}),
      finish: ownDefault || (sold(familyDefault) ? familyDefault : null),
    },
  };
}

/**
 * `offering` narrowed to the finishes the door `look` wears is sold in. A
 * DOORLESS cabinet (a filler, an open shelf) has no door to narrow by — the
 * flat its missing style resolves to is not its product — so it is judged
 * against everything its family sells. Narrowing a filler by "flat" put a
 * Tasoro thin-shaker run's fillers in Flat Panel Champagne.
 */
function offeringForLook(offering, look) {
  if (!offering || isDoorlessCabinet(look)) return offering;
  return offeringForDesign(offering, designForLook(offering, look));
}

const woodOf = (look) =>
  (look && look.texture && look.texture.textureId) || null;
// A cabinet is painted OR textured, and the texture wins when both are set
// (`cabinet-finish`), so paint only counts when there is no wood.
const paintOf = (look) => (!woodOf(look) && look && look.paintColor) || null;

/**
 * What `look` wears that `offering` does not sell, as
 * `[{ kind: 'door' | 'paint' | 'wood', value, label }]`. Empty when the whole
 * look is offered — and always empty under an unrestricted list. A finish is
 * judged against what the look's DOOR is sold in (`offeringForLook`): a colour
 * the family sells on another door only is a miss, and says which door
 * (`withDesign`).
 */
function lookNotOffered(familyOffering, look) {
  const misses = [];
  const offering = offeringForLook(familyOffering, look);
  // Said only where a door narrowed the finishes.
  const onDoor = offering.design ? { withDesign: offering.design } : {};
  // A doorless cabinet has no door to be "not offered".
  const hasDoor = !isDoorlessCabinet(look);
  if (hasDoor && offering.restricts.door && !designForLook(offering, look)) {
    const drawn = resolveDoorStyle(look && look.doorStyle);
    misses.push({
      kind: 'door',
      value: (look && look.doorDesign) || drawn,
      label:
        DRAWN_DOOR_STYLES.find((d) => d.id === drawn)?.name ||
        (look && look.doorDesign) ||
        drawn,
    });
  }
  const paint = paintOf(look);
  if (
    paint &&
    offering.restricts.paint &&
    !offering.paint.some((p) => sameHex(p.hex, paint))
  ) {
    const known =
      familyOffering.paint.find((p) => sameHex(p.hex, paint)) ||
      ALL_PAINT.find((p) => sameHex(p.hex, paint));
    misses.push({
      kind: 'paint',
      value: paint,
      label: known ? known.name : paint,
      ...onDoor,
    });
  }
  const wood = woodOf(look);
  if (wood && offering.restricts.wood && !offering.woods.includes(wood)) {
    misses.push({
      kind: 'wood',
      value: wood,
      label: 'this wood finish',
      ...onDoor,
    });
  }
  return misses;
}

/** A finish bundle for an offering's default (or first) finish, or null. */
function defaultFinishOf(offering, paintFinish) {
  const want = (offering.defaults && offering.defaults.finish) || null;
  const paintEntry =
    (want && want.paint && offering.paint.find((p) => p.id === want.paint)) ||
    null;
  const woodId =
    want && want.wood && offering.woods && offering.woods.includes(want.wood)
      ? want.wood
      : null;
  if (paintEntry) {
    return {
      paintColor: paintEntry.hex,
      paintFinish: paintEntry.paintFinish || paintFinish || 'satin',
      texture: null,
    };
  }
  if (woodId)
    return {
      texture: { textureId: woodId },
      paintColor: null,
      paintFinish: null,
    };
  if (offering.restricts.paint && offering.paint.length) {
    return {
      paintColor: offering.paint[0].hex,
      paintFinish: offering.paint[0].paintFinish || paintFinish || 'satin',
      texture: null,
    };
  }
  if (offering.woods && offering.woods.length) {
    return {
      texture: { textureId: offering.woods[0] },
      paintColor: null,
      paintFinish: null,
    };
  }
  return null;
}

/**
 * `look` moved inside `offering` — for a tool CHOOSING a look anew (a style
 * preset), never for a stored cabinet (those are flagged, not changed).
 *
 * The door keeps its design when sold, else the design that draws the same,
 * else the family default; the finish keeps its colour or wood when sold, else
 * takes the family default finish. Null-valued keys are an explicit "not this"
 * (the `cabinet-finish` convention), so a caller writing the bundle clears the
 * other half of the finish.
 */
function mapLookToOffering(look, offering) {
  return mapFinishToOffering(mapDoorToOffering(look, offering), offering);
}

/** Only the DOOR half of `mapLookToOffering`; the finish is left alone. */
function mapDoorToOffering(look, offering) {
  const next = { ...(look || {}) };
  // A doorless cabinet is given no door.
  if (isDoorlessCabinet(next)) return next;
  if (!offering.restricts.door) {
    // Every door is sold, so the door stays — but another vendor's design id
    // does not belong to this family: record the design it draws as.
    const recorded = next.doorDesign;
    if (recorded && !offering.doorDesigns.some((d) => d.id === recorded)) {
      next.doorDesign = resolveDoorStyle(next.doorStyle);
    }
    return next;
  }
  const designs = offering.doorDesigns;
  const pick =
    designForLook(offering, next) ||
    // Another vendor's design is not this family's, but a design of this
    // family's that draws the same is the closest it sells.
    designForLook(offering, { doorStyle: next.doorStyle }) ||
    designs.find(
      (d) => d.id === (offering.defaults && offering.defaults.doorDesign)
    ) ||
    designs[0] ||
    null;
  if (pick) {
    next.doorDesign = pick.id;
    next.doorStyle = pick.renders;
  }
  return next;
}

/**
 * Only the FINISH half: a paint or wood the family does not sell becomes its
 * default finish; the door is left alone. For a tool that sets a cabinet's
 * finish and nothing else (`apply_finishes`), which must not touch a stored
 * door the family does not sell — that is flagged, not changed.
 */
function mapFinishToOffering(look, offering) {
  const next = { ...(look || {}) };
  const finishMisses = lookNotOffered(offering, next).filter(
    (m) => m.kind === 'paint' || m.kind === 'wood'
  );
  if (finishMisses.length) {
    // The default of what the look's door is SOLD in, not the family's: a
    // family default sold on another door would only be a miss again.
    const finish = defaultFinishOf(
      offeringForLook(offering, next),
      next.paintFinish
    );
    if (finish) Object.assign(next, finish);
  }
  return next;
}

const FINISH_KEYS = ['paintColor', 'paintFinish', 'texture'];

/**
 * What picking door design `designId` writes onto a cabinet wearing `look`:
 * the design and the style it draws as, plus — only where the door is not sold
 * in what the cabinet wears — that door's default finish. A cabinet with no
 * finish of its own (it shows the type's default wood) takes the door's
 * default too, when the door names its finishes: that default wood is not
 * one of them. Finish keys that do not change are left out, so the write
 * touches nothing else. Null when the offering has no such design.
 */
function lookForDoorPick(look, offering, designId) {
  const design = offering.doorDesigns.find((d) => d.id === designId);
  if (!design) return null;
  const write = { doorDesign: design.id, doorStyle: design.renders };
  const before = { ...(look || {}), ...write };
  const ownFinish = !!(woodOf(before) || before.paintColor);
  let after = mapFinishToOffering(before, offering);
  if (!ownFinish && design.finishes) {
    const finish = defaultFinishOf(
      offeringForDesign(offering, design),
      before.paintFinish
    );
    if (finish) after = { ...after, ...finish };
  }
  for (const k of FINISH_KEYS) {
    const was = before[k] === undefined ? null : before[k];
    const now = after[k] === undefined ? null : after[k];
    if (now !== was) write[k] = now;
  }
  return write;
}

/**
 * `look` carried into a family it is being SWITCHED to: the door becomes the
 * family's design that draws the same (else its default), and the finish is
 * kept where that door is sold in it, else becomes the door's (or the
 * family's) default — `lookForDoorPick` with the door the family maps it to.
 * A switch is chosen, so landing on what the family sells is the point.
 * Returns the whole look; untouched keys pass through, and a finish half it
 * clears is null (the `cabinet-finish` "explicit not this").
 */
function lookForFamily(look, offering) {
  const base = { ...(look || {}) };
  // A doorless cabinet keeps no door to carry: its finish alone moves.
  if (isDoorlessCabinet(base)) {
    return { ...base, ...mapFinishToOffering(base, offering) };
  }
  const pick = mapDoorToOffering(base, offering).doorDesign;
  const write =
    (pick && lookForDoorPick(base, offering, pick)) ||
    mapFinishToOffering(base, offering);
  return { ...base, ...write };
}

/**
 * `obj` wearing `look`: each key set, and a NULL-valued key removed — the
 * `cabinet-finish` "explicit not this", so painting clears the texture and
 * texturing clears the paint. Pure; `obj` is not mutated.
 */
function overlayLook(obj, look) {
  const next = { ...obj };
  for (const [k, v] of Object.entries(look || {})) {
    if (v === null) delete next[k];
    else if (v !== undefined) next[k] = v;
  }
  return next;
}

/**
 * What EVERY one of several offerings sells — for a ROOM-scope look across runs
 * of different families. An unrestricted list sells everything, so it never
 * narrows the result; only restricting offerings are intersected.
 */
function intersectOfferings(offerings) {
  const list = (offerings || []).filter(Boolean);
  if (list.length === 1) return list[0];
  const result = offeringOf(null);
  result.familyId = null;
  result.familyName = null;
  result.explain = list.some((o) => o.explain);
  const doorSets = list.filter((o) => o.restricts.door);
  if (doorSets.length) {
    result.restricts.door = true;
    result.doorDesigns = doorSets[0].doorDesigns.filter((d) =>
      doorSets.every((o) => o.doorDesigns.some((x) => x.id === d.id))
    );
  }
  const paintSets = list.filter((o) => o.restricts.paint);
  if (paintSets.length) {
    result.restricts.paint = true;
    result.paint = paintSets[0].paint.filter((p) =>
      paintSets.every((o) => o.paint.some((x) => sameHex(x.hex, p.hex)))
    );
  }
  const woodSets = list.filter((o) => o.restricts.wood);
  if (woodSets.length) {
    result.restricts.wood = true;
    result.woods = woodSets[0].woods.filter((w) =>
      woodSets.every((o) => o.woods.includes(w))
    );
  }
  return result;
}

/** The offering every run in a room shares (a room with no runs: unrestricted). */
function offeringForRoom(room, companyOffering) {
  const runs = (room && room.cabinetRuns) || [];
  if (!runs.length) return offeringOf(null);
  return intersectOfferings(
    runs.map((run) => offeringForRun(run, companyOffering))
  );
}

/**
 * The room offering narrowed to one door's finishes when EVERY run cabinet in
 * the room wears the same door — a room-wide finish lands on all of them, so
 * it may be narrowed to a door only when there is just the one. Mixed doors
 * keep the room offering (each cabinet is then judged on its own door).
 */
function offeringForRoomDoors(room, companyOffering) {
  const offering = offeringForRoom(room, companyOffering);
  const cabinets = ((room && room.objects) || []).filter((o) => o && o.runId);
  const ids = new Set(
    cabinets.map((o) => (designForLook(offering, o) || {}).id || null)
  );
  if (ids.size !== 1) return offering;
  const [only] = ids;
  return offeringForDesign(
    offering,
    offering.doorDesigns.find((d) => d.id === only) || null
  );
}

const FILLS = ['continuous', 'grid', 'widths'];
const HEX = /^#[0-9a-f]{6}$/i;
// A colour's photo: a picture the web app serves itself (`web-app/public`),
// so it ships with hosting to every environment.
const SITE_PICTURE = /^\/[\w./-]+\.(jpe?g|png|webp)$/i;
const isAll = (v) => v === 'all';

/**
 * The problems with a family definition, as strings; empty when it is sound.
 * The contract a family DATA FILE must meet — `__tests__/familyFiles.ci.test.js`
 * runs it over every file. Wood ids are checked for shape only: whether each
 * exists in the catalog is a question for Firestore, not a unit test.
 */
function validateFamily(f) {
  const errs = [];
  const at = (msg) => errs.push(`${(f && f.id) || '(no id)'}: ${msg}`);
  if (!f || typeof f.id !== 'string' || !f.id) return ['family has no id'];
  if (typeof f.name !== 'string' || !f.name) at('no name');
  const spec = f.spec || {};
  if (!FILLS.includes(spec.fill))
    at(`spec.fill must be one of ${FILLS.join(', ')}`);
  if (!Array.isArray(spec.widths) || spec.widths.some((w) => !(w > 0))) {
    at('spec.widths must be a list of positive inches');
  } else if (spec.widths.some((w, i) => i && w <= spec.widths[i - 1])) {
    at('spec.widths must ascend');
  }
  if (spec.fill === 'widths' && !(spec.widths || []).length) {
    at('a "widths" family must list its widths');
  }
  if (!CORNER_BLIND_SETS[spec.cornerBlind]) {
    at(`spec.cornerBlind "${spec.cornerBlind}" is not a corner-blind set`);
  }
  if (spec.sizes !== undefined && !VENDOR_SIZE_SETS[spec.sizes]) {
    at(`spec.sizes "${spec.sizes}" is not a vendor size catalog`);
  }
  if (spec.depths !== undefined && !DEPTH_RULES.includes(spec.depths)) {
    at(`spec.depths must be one of ${DEPTH_RULES.join(', ')}`);
  }
  if (spec.bodyHeights !== undefined) {
    const bh = spec.bodyHeights;
    if (!bh || typeof bh !== 'object' || Array.isArray(bh))
      at('spec.bodyHeights must map a profile id to inches');
    else
      for (const [profileId, inches] of Object.entries(bh))
        if (!(inches > 0))
          at(
            `spec.bodyHeights.${profileId} must be a positive height in inches`
          );
  }

  const o = f.offering;
  if (!o) return [...errs, `${f.id}: no offering`];
  const ids = (list) => list.map((x) => x && x.id);
  const dupes = (list) => list.filter((x, i) => list.indexOf(x) !== i);

  if (!isAll(o.doorDesigns)) {
    if (!Array.isArray(o.doorDesigns) || !o.doorDesigns.length) {
      at('offering.doorDesigns must be "all" or a non-empty list');
    } else {
      for (const d of o.doorDesigns) {
        if (!d || !d.id || !d.name)
          at('every door design needs an id and a name');
        else if (!DRAWN_DOOR_STYLE_IDS.includes(d.renders)) {
          at(
            `door design "${d.id}" renders "${d.renders}", which is not a drawn style (${DRAWN_DOOR_STYLE_IDS.join(', ')})`
          );
        }
        if (d && d.id && d.glass !== undefined && typeof d.glass !== 'boolean')
          at(`door design "${d.id}" glass must be true or false`);
      }
      if (dupes(ids(o.doorDesigns)).length)
        at('door design ids must be unique');
    }
  }
  if (!isAll(o.paint)) {
    if (!Array.isArray(o.paint)) {
      at('offering.paint must be "all" or a list');
    } else {
      for (const p of o.paint) {
        if (!p || !p.id || !p.name)
          at('every paint colour needs an id and a name');
        else if (!HEX.test(p.hex || ''))
          at(`paint "${p.id}" needs a #rrggbb hex`);
        else if (p.paintFinish && !PAINT_FINISHES.includes(p.paintFinish))
          at(
            `paint "${p.id}" has sheen "${p.paintFinish}", not one of ${PAINT_FINISHES.join(', ')}`
          );
        else if (p.photo !== undefined && !SITE_PICTURE.test(String(p.photo)))
          at(
            `paint "${p.id}" photo ${JSON.stringify(p.photo)} is not a picture on the web app (a /path in web-app/public)`
          );
      }
      if (dupes(ids(o.paint)).length) at('paint ids must be unique');
      // A vendor's colour code (`code`) is the key its price list is read by.
      const codes = o.paint.map((p) => p && p.code).filter(Boolean);
      for (const c of codes)
        if (!/^[A-Z]{1,4}$/.test(c))
          at(`colour code "${c}" is not 1–4 capital letters`);
      if (dupes(codes).length) at('colour codes must be unique');
    }
  }
  if (!isAll(o.woods)) {
    if (
      !Array.isArray(o.woods) ||
      o.woods.some((w) => typeof w !== 'string' || !w)
    ) {
      at('offering.woods must be "all" or a list of catalog texture ids');
    } else if (dupes(o.woods).length) {
      at('wood ids must be unique');
    }
  }
  if (
    Array.isArray(o.paint) &&
    Array.isArray(o.woods) &&
    !o.paint.length &&
    !o.woods.length
  ) {
    at('a family must sell at least one finish');
  }

  if (Array.isArray(o.doorDesigns)) {
    for (const design of o.doorDesigns) {
      if (design && design.id)
        errs.push(...designFinishErrors(f.id, o, design));
    }
  }

  const d = o.defaults;
  if (d) {
    if (
      d.doorDesign &&
      Array.isArray(o.doorDesigns) &&
      !o.doorDesigns.some((x) => x.id === d.doorDesign)
    ) {
      at(`default door design "${d.doorDesign}" is not in its door designs`);
    }
    if (
      d.doorDesign &&
      isAll(o.doorDesigns) &&
      !DRAWN_DOOR_STYLE_IDS.includes(d.doorDesign)
    ) {
      at(`default door design "${d.doorDesign}" is not a drawn style`);
    }
    const fin = d.finish;
    if (fin) {
      const keys = ['paint', 'wood'].filter((k) => fin[k]);
      if (keys.length !== 1)
        at('a default finish names exactly one of paint or wood');
      if (
        fin.paint &&
        Array.isArray(o.paint) &&
        !o.paint.some((p) => p.id === fin.paint)
      ) {
        at(`default paint "${fin.paint}" is not in its paint list`);
      }
      if (fin.wood && Array.isArray(o.woods) && !o.woods.includes(fin.wood)) {
        at(`default wood "${fin.wood}" is not in its woods`);
      }
      const design =
        d.doorDesign &&
        Array.isArray(o.doorDesigns) &&
        o.doorDesigns.find((x) => x.id === d.doorDesign);
      if (design && design.finishes && !designSells(design, fin)) {
        at(
          `the default finish is not sold with the default door design "${design.id}"`
        );
      }
    }
  }
  return errs;
}

const PAINT_FINISHES = ['matte', 'satin', 'gloss', 'high-gloss'];

/** Is `finish` (`{ paint }` or `{ wood }`) one of the finishes `design` lists? */
const designSells = (design, finish) =>
  !!finish &&
  ((!!finish.paint && (design.finishes.paint || []).includes(finish.paint)) ||
    (!!finish.wood && (design.finishes.woods || []).includes(finish.wood)));

/**
 * The problems with one door design's own finish list: each must be a finish
 * its family sells, it must sell at least one, and its default must be one of
 * them.
 */
function designFinishErrors(familyId, o, design) {
  const errs = [];
  const at = (msg) =>
    errs.push(`${familyId}: door design "${design.id}" ${msg}`);
  const fin = design.finishes;
  if (fin === undefined) {
    if (design.defaultFinish) at('has a defaultFinish but no finishes');
    return errs;
  }
  if (!fin || typeof fin !== 'object' || Array.isArray(fin)) {
    return [
      `${familyId}: door design "${design.id}" finishes must be { paint, woods }`,
    ];
  }
  const paint = fin.paint || [];
  const woods = fin.woods || [];
  if (!Array.isArray(paint) || !Array.isArray(woods)) {
    return [
      `${familyId}: door design "${design.id}" finishes.paint / .woods must be lists`,
    ];
  }
  if (!paint.length && !woods.length) at('sells no finish');
  const familyPaint = isAll(o.paint) ? ALL_PAINT : o.paint || [];
  for (const id of paint) {
    if (!familyPaint.some((p) => p && p.id === id))
      at(`sells paint "${id}", which its family does not`);
  }
  if (Array.isArray(o.woods)) {
    for (const id of woods) {
      if (!o.woods.includes(id))
        at(`sells wood "${id}", which its family does not`);
    }
  }
  const def = design.defaultFinish;
  if (def) {
    const keys = ['paint', 'wood'].filter((k) => def[k]);
    if (keys.length !== 1)
      at('defaultFinish names exactly one of paint or wood');
    else if (!designSells(design, def))
      at('defaultFinish is not one of its finishes');
  }
  return errs;
}

module.exports = {
  ALL_DOOR_DESIGNS,
  ALL_PAINT,
  offeringOf,
  offeringForRun,
  offeringForCabinet,
  offeringForRoom,
  offeringForRoomDoors,
  intersectOfferings,
  designForLook,
  designSellsGlass,
  offeringForDesign,
  offeringForLook,
  lookNotOffered,
  mapLookToOffering,
  mapDoorToOffering,
  mapFinishToOffering,
  lookForDoorPick,
  lookForFamily,
  overlayLook,
  validateFamily,
};
